/** @jsxImportSource @emotion/react */
import { ClassNames } from "@emotion/react";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import { Alert as MuiAlert } from "@material-ui/lab";
import { forwardRef, ReactNode } from "react";

import { StylableProps } from "../../types";
import { Typography } from "../typography";

export interface AlertProps extends StylableProps {
  severity: "success" | "info" | "error" | "warning";
  title?: ReactNode;
  message: ReactNode;
  displayTitleAndMessageOnOneLine?: boolean;
  variant?: "filled";
  messageTypographyVariant?: "body" | "footnote";
  onClose?: () => void;
  customIcon?: ReactNode;
  alignIcon?: "top" | "center";
}

/**
 * Displays an alert message to the user
 */
export const Alert = forwardRef(
  (
    {
      className,
      title,
      severity,
      message,
      variant,
      onClose,
      customIcon,
      messageTypographyVariant = "body",
      displayTitleAndMessageOnOneLine = false,
      alignIcon = "center",
    }: AlertProps,
    ref
  ): JSX.Element => {
    return (
      <ClassNames>
        {({ css, cx, theme }) => (
          <MuiAlert
            ref={ref}
            variant={variant}
            onClose={onClose}
            icon={
              customIcon ? (
                customIcon
              ) : (
                <>
                  {severity === "success" && (
                    <CheckCircleIcon fontSize="inherit" />
                  )}
                  {severity === "info" && (
                    <InfoOutlinedIcon
                      className={css`
                        ${!variant &&
                        css`
                          color: #616161;
                        `}
                      `}
                      fontSize="inherit"
                    />
                  )}
                  {severity === "error" && (
                    <ErrorOutlineIcon fontSize="inherit" />
                  )}
                  {severity === "warning" && (
                    <ReportProblemOutlinedIcon fontSize="inherit" />
                  )}
                </>
              )
            }
            classes={{
              icon: css`
                display: flex;
                align-items: center;
                ${alignIcon === "top" &&
                css`
                  align-self: flex-start;
                `}
              `,
              message: css`
                ${displayTitleAndMessageOnOneLine &&
                css`
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  gap: ${theme.spacing(1)};
                `}
              `,
            }}
            className={cx(
              css`
                text-align: left;
                ${severity === "info" &&
                !variant &&
                css`
                  background-color: #f5f9fa;
                `}
              `,
              className
            )}
            severity={severity}
          >
            {title && (
              <Typography
                variant="subtitle"
                color="textPrimary"
                css={
                  !displayTitleAndMessageOnOneLine &&
                  css`
                    margin-top: -4px;
                  `
                }
              >
                {title}
              </Typography>
            )}
            <Typography
              variant={messageTypographyVariant}
              color={title && severity === "info" ? "grey.800" : undefined}
            >
              {message}
            </Typography>
          </MuiAlert>
        )}
      </ClassNames>
    );
  }
);
